import React, { useState } from 'react';
import { HelpCircle, Shield, Lock, FileText, ShieldCheck } from 'lucide-react';

// Legal Documents Content (would normally be in separate files)
const legalDocuments = {
  terms: {
    title: "Terms of Service",
    content: (
      <div className="space-y-4">
        <h2 className="text-xl font-bold">SecureErase Pro Terms of Service</h2>
        <p className="text-sm">Last Updated: {new Date().toLocaleDateString()}</p>
        
        <section>
          <h3 className="font-semibold mt-4">1. License Grant</h3>
          <p className="text-sm mt-2">
            SecureErase Pro grants you a limited, non-exclusive, non-transferable license to use the software...
          </p>
        </section>

        <section>
          <h3 className="font-semibold mt-4">2. Data Responsibility</h3>
          <p className="text-sm mt-2">
            You acknowledge that secure deletion of files is irreversible. SecureErase Pro is not responsible for...
          </p>
        </section>

        {/* More sections... */}
      </div>
    )
  },
  privacy: {
    title: "Privacy Policy",
    content: (
      <div className="space-y-4">
        <h2 className="text-xl font-bold">Privacy Policy</h2>
        <p className="text-sm">Effective: {new Date().toLocaleDateString()}</p>
        
        <section>
          <h3 className="font-semibold mt-4">1. Information We Collect</h3>
          <p className="text-sm mt-2">
            SecureErase Pro collects minimal operational data including version information and...
          </p>
        </section>

        <section>
          <h3 className="font-semibold mt-4">2. Data Security</h3>
          <p className="text-sm mt-2">
            We implement industry-standard security measures to protect any collected information...
          </p>
        </section>

        {/* More sections... */}
      </div>
    )
  },
  compliance: {
    title: "Compliance Information",
    content: (
      <div className="space-y-4">
        <h2 className="text-xl font-bold">Regulatory Compliance</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
            <h3 className="font-medium flex items-center">
              <ShieldCheck className="mr-2 text-green-400" size={18} />
              Standards Compliance
            </h3>
            <ul className="mt-2 space-y-2 text-sm">
              <li className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span>DoD 5220.22-M (3-pass)</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span>NIST SP 800-88 Rev. 1</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span>GDPR Data Erasure Requirements</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
            <h3 className="font-medium flex items-center">
              <FileText className="mr-2 text-blue-400" size={18} />
              Certifications
            </h3>
            <ul className="mt-2 space-y-2 text-sm">
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">•</span>
                <span>ISO/IEC 27001:2022</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">•</span>
                <span>Common Criteria EAL2+</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    )
  }
};

// Legal Modal Component
const LegalModal = ({ documentType, onClose }) => {
  const document = legalDocuments[documentType];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-800 shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="border-b border-gray-800 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">{document.title}</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800"
          >
            ✕
          </button>
        </div>
        
        <div className="overflow-y-auto p-6">
          {document.content}
        </div>
        
        <div className="border-t border-gray-800 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Footer Component
const Footer = () => {
  const [activeLegalDoc, setActiveLegalDoc] = useState(null);
  const currentYear = new Date().getFullYear();

  return (
    <>
      <footer className="flex-1 p-6 ml-72 bg-gray-950 text-white relative">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Product Info */}
            <div>
              <h3 className="text-gray-300 font-medium mb-4 flex items-center">
                <Shield className="mr-2 text-blue-400" size={18} />
                SecureErase Pro
              </h3>
              <p className="text-sm text-gray-400">
                Enterprise-grade secure deletion solution meeting military and regulatory standards.
              </p>
              <div className="mt-4 text-xs text-gray-500">
                Version 2.1.0 • Build {currentYear}.06.15
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-gray-300 font-medium mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button 
                    onClick={() => setActiveLegalDoc('terms')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setActiveLegalDoc('privacy')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setActiveLegalDoc('compliance')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Compliance Info
                  </button>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Documentation
                  </a>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-gray-300 font-medium mb-4">Support</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Contact Support
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Knowledge Base
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    System Requirements
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Release Notes
                  </a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-gray-300 font-medium mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Press
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-700/50 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-500">
              © {currentYear} SecureErase Pro. All rights reserved.
            </div>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <span className="text-xs text-gray-500">
                Developed by F/N Nuru The_Geek
              </span>
              <div className="flex space-x-3">
                <a href="#" className="text-gray-500 hover:text-white transition-colors">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
                  </svg>
                </a>
                <a href="#" className="text-gray-500 hover:text-white transition-colors">
                  <span className="sr-only">GitHub</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Document Modals */}
      {activeLegalDoc && (
        <LegalModal 
          documentType={activeLegalDoc} 
          onClose={() => setActiveLegalDoc(null)} 
        />
      )}
    </>
  );
};

export default Footer;